
class ShopFeedNewPopup extends HTMLElement {
  constructor() {
    super();
    this.isOpen = false;
    this._mounted = false;
    this._infoCache = {};
    this._soundOn = false;
    this._onKeydown = this._onKeydown.bind(this);
  }

  connectedCallback() {
    if (this._initialized) return;
    this._initialized = true;

    this.template = this.querySelector('[data-sfn-template]');

    const scope = this.closest('.shopify-section') || document;
    this.slideTemplates = Array.from(scope.querySelectorAll('template[data-sfn-slide]'));

    const openers = Array.from(scope.querySelectorAll('[data-sfn-open]'));

    const viewportObserver = 'IntersectionObserver' in window
      ? new IntersectionObserver((entries, obs) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            const opener = entry.target;
            this.prefetchInfo(opener.dataset.sfnBlock, opener.dataset.sfnUrl);
            obs.unobserve(opener);
          });
        }, { rootMargin: '200px' })
      : null;

    openers.forEach((opener) => {
      opener.addEventListener('click', (e) => {
        e.preventDefault();
        this.open(opener.dataset.sfnBlock);
      });
      const warm = () => this.prefetchInfo(opener.dataset.sfnBlock, opener.dataset.sfnUrl);
      opener.addEventListener('pointerenter', warm, { once: true });
      opener.addEventListener('touchstart', warm, { once: true, passive: true });
      if (viewportObserver) viewportObserver.observe(opener);
    });
  }

  mount() {
    if (this._mounted) return;
    if (this.parentElement !== document.body) document.body.appendChild(this);

    this.innerHTML = this.template ? this.template.innerHTML : '';

    const list = this.querySelector('[data-sfn-slides]');
    if (list) {
      this.slideTemplates.forEach((tpl) => list.appendChild(tpl.content.cloneNode(true)));
    }

    this._mounted = true;

    this.dialog = this.querySelector('.shop-feed-new-popup__dialog');
    this.slider = this.querySelector('slider-component');
    this.prevBtns = Array.from(this.querySelectorAll('[data-sfn-prev]'));
    this.nextBtns = Array.from(this.querySelectorAll('[data-sfn-next]'));
    this.progressCounter = this.querySelector('[data-sfn-progress]');

    this.addEventListener('click', (e) => {
      if (e.target.closest('[data-sfn-close]')) {
        e.preventDefault();
        this.close();
        return;
      }
      if (e.target.closest('[data-sfn-sound]')) {
        e.preventDefault();
        this.toggleSound();
      }
    });
    this.prevBtns.forEach((b) => b.addEventListener('click', () => this.slider?.navigateToPreviousSlide?.()));
    this.nextBtns.forEach((b) => b.addEventListener('click', () => this.slider?.navigateToNextSlide?.()));
    this.slider?.addEventListener('slideChanged', (e) => this.onActiveSlide(e.detail?.currentElement));

    if (this.dialog) {
      this.dialog.addEventListener('scroll', () => this.onDialogScroll(), { passive: true });
    }

    this._onCartUpdated = () => {
      if (this.isOpen) this.close();
    };
    document.addEventListener('cart:updated', this._onCartUpdated);
  }

  slides() {
    return this.slider
      ? Array.from(this.slider.querySelectorAll('.shop-feed-new-popup__slide'))
      : [];
  }

  isMobilePopup() {
    return window.matchMedia('(max-width: 767px) and (orientation: portrait)').matches;
  }

  updateMediaMetrics(slide) {
    if (!slide || !this.isMobilePopup()) {
      this._mediaH = 0;
      return;
    }
    requestAnimationFrame(() => {
      const media = slide.querySelector('[data-sfn-media]');
      const h = media ? media.offsetHeight : 0;
      if (h) this.style.setProperty('--sfn-media-h', `${h}px`);
      this._mediaH = h;
      this.onDialogScroll();
    });
  }

  onDialogScroll() {
    if (!this.isMobilePopup()) {
      this.classList.remove('is-scrolled');
      return;
    }
    const threshold = (this._mediaH || 0) * 0.5;
    this.classList.toggle('is-scrolled', (this.dialog?.scrollTop || 0) > threshold);
  }

  refreshSliderHeight() {
    if (!this.isMobilePopup()) return;
    requestAnimationFrame(() => {
      try {
        this.slider?.scheduleSetHeight?.(true);
      } catch (err) {

      }
    });
  }

  open(blockId) {
    this.mount();

    this.hidden = false;
    void this.offsetWidth;
    this.classList.add('is-open');
    this.isOpen = true;
    document.body.classList.add('overflow-hidden');
    document.addEventListener('keydown', this._onKeydown);

    const start = () => {
      const slides = this.slides();
      let index = slides.findIndex((s) => s.dataset.blockId === blockId);
      if (index < 0) index = 0;

      this.slider?.reInit?.();
      this.slider?.goToSlide?.(index, false);
      this.onActiveSlide(slides[index]);
    };

    if (customElements.get('slider-component')) start();
    else customElements.whenDefined('slider-component').then(start);
  }

  onActiveSlide(activeSlide) {
    const slides = this.slides();
    if (!activeSlide) activeSlide = slides[this.slider?.currentIndex || 0];
    if (!activeSlide) return;

    const mobile = this.isMobilePopup();

    slides.forEach((slide) => {
      if (slide === activeSlide) return;
      const otherDm = slide.querySelector('[data-sfn-media] deferred-media');
      otherDm?.pauseMedia?.();
      const otherVideo = otherDm?.querySelector('video');
      if (otherVideo) otherVideo.muted = true;
      if (!mobile) this.unmountInfo(slide);
    });

    const dm = activeSlide.querySelector('[data-sfn-media] deferred-media');
    if (dm) {
      const activate = () => {
        try {
          if (dm._userPaused) return;
          if (!dm.hasAttribute('data-media-loaded')) dm.loadContent?.(true);
          else dm.playMedia?.();
        } catch (err) {

        }
      };
      if (customElements.get('deferred-media')) activate();
      else customElements.whenDefined('deferred-media').then(activate);
      this.applySoundWhenReady(dm);
    }

    this.updateSoundButtons();

    this.loadInfo(activeSlide);
    this.updateArrows(slides, activeSlide);
    this.updateMediaMetrics(activeSlide);

    const active = slides.indexOf(activeSlide);
    const defer = (fn) => (window.requestIdleCallback ? requestIdleCallback(fn, { timeout: 800 }) : setTimeout(fn, 400));
    [slides[active - 1], slides[active + 1]].forEach((s) => {
      if (!s) return;
      if (mobile) {
        defer(() => this.loadInfo(s));
      } else {
        const c = s.querySelector('[data-sfn-info]');
        if (c) this.prefetchInfo(s.dataset.blockId, c.dataset.productUrl);
      }
    });
  }

  toggleSound() {
    this._soundOn = !this._soundOn;

    const activeSlide = this.slides()[this.slider?.currentIndex || 0];
    const dm = activeSlide?.querySelector('[data-sfn-media] deferred-media');
    const video = dm?.querySelector('video');
    if (video) {
      video.muted = !this._soundOn;
      if (this._soundOn) video.play?.().catch(() => {});
    }

    this.updateSoundButtons();
  }

  applySoundWhenReady(dm) {
    if (!dm) return;
    const apply = () => {
      const video = dm.querySelector('video');
      if (!video) return false;
      video.muted = !this._soundOn;
      return true;
    };
    if (apply()) return;

    let tries = 0;
    const id = window.setInterval(() => {
      if (apply() || ++tries > 40) window.clearInterval(id);
    }, 100);
  }

  updateSoundButtons() {
    const on = this._soundOn;
    this.querySelectorAll('[data-sfn-sound]').forEach((btn) => {
      btn.setAttribute('aria-pressed', on ? 'true' : 'false');
      const label = on ? btn.dataset.labelMute : btn.dataset.labelUnmute;
      if (label) btn.setAttribute('aria-label', label);
      btn.querySelector('[data-sfn-sound-off]')?.toggleAttribute('hidden', on);
      btn.querySelector('[data-sfn-sound-on]')?.toggleAttribute('hidden', !on);
    });
  }

  updateArrows(slides, activeSlide) {
    const index = slides.indexOf(activeSlide);
    const total = slides.length;
    this.classList.toggle('shop-feed-new-popup--single', total <= 1);
    this.prevBtns?.forEach((b) => b.toggleAttribute('disabled', index <= 0));
    this.nextBtns?.forEach((b) => b.toggleAttribute('disabled', index >= total - 1));
    if (this.progressCounter) {
      this.progressCounter.style.setProperty('--slider-counter-current', index + 1);
      this.progressCounter.style.setProperty('--slider-counter-total', total);
    }
  }

  fetchInfoHTML(blockId, rawUrl) {
    if (this._infoCache[blockId]) return Promise.resolve(this._infoCache[blockId]);
    if (!rawUrl) return Promise.resolve(null);
    if (!this._infoPending) this._infoPending = {};
    if (this._infoPending[blockId]) return this._infoPending[blockId];

    const promise = (async () => {
      const res = await fetch(`${rawUrl.split('?')[0]}?view=quickview`);
      if (!res.ok) throw new Error('Failed to load product');
      const doc = new DOMParser().parseFromString(await res.text(), 'text/html');
      const productInfo = doc.querySelector('product-info');
      if (!productInfo) return null;
      productInfo.setAttribute('data-update-url', 'false');
      productInfo.querySelectorAll('animated-stages').forEach((el) => el.setAttribute('data-instant', ''));
      const dataUrl = productInfo.getAttribute('data-url');
      if (dataUrl && !/[?&]view=quickview(?:&|$)/.test(dataUrl)) {
        const sep = dataUrl.includes('?') ? '&' : '?';
        productInfo.setAttribute('data-url', `${dataUrl}${sep}view=quickview`);
      }
      this._infoCache[blockId] = productInfo.outerHTML;
      return this._infoCache[blockId];
    })();

    this._infoPending[blockId] = promise;
    promise.catch(() => {}).finally(() => delete this._infoPending[blockId]);
    return promise;
  }

  prefetchInfo(blockId, rawUrl) {
    if (blockId && rawUrl) this.fetchInfoHTML(blockId, rawUrl).catch(() => {});
  }

  async loadInfo(slide) {
    const container = slide.querySelector('[data-sfn-info]');
    if (!container || container.dataset.sfnLoaded === 'true') return;

    const blockId = slide.dataset.blockId;
    const rawUrl = container.dataset.productUrl;
    if (!rawUrl) return;

    container.dataset.sfnLoaded = 'true';

    try {
      const productInfoHTML = await this.fetchInfoHTML(blockId, rawUrl);
      if (!productInfoHTML) {
        container.innerHTML = '<p>Product not found</p>';
        return;
      }

      if (container.dataset.sfnLoaded !== 'true') return;

      if (this.isMobilePopup()) {
        const tmp = document.createElement('div');
        tmp.innerHTML = productInfoHTML;
        tmp.querySelectorAll('animated-stages, animated-stages-slider').forEach((el) => {
          el.replaceWith(...el.childNodes);
        });
        tmp.querySelectorAll('[data-animate]').forEach((el) => el.removeAttribute('data-animate'));
        container.innerHTML = tmp.innerHTML;
      } else {
        container.innerHTML = productInfoHTML;
      }
      this.trimInfoContent(container);
      this.simplifyActions(container);
      this.pinFooter(container);

      this.refreshSliderHeight();

      await this.loadExternalScripts(container);
      if (window.Shopify && Shopify.PaymentButton) Shopify.PaymentButton.init();
      if (window.ProductModel) window.ProductModel.loadShopifyXR();

      this.refreshSliderHeight();

      const scrollbar = slide.querySelector('custom-scrollbar');
      if (scrollbar) {
        const initScrollbar = () => requestAnimationFrame(() => {
          try {
            scrollbar.init?.();
          } catch (err) {

          }
        });
        if (customElements.get('custom-scrollbar')) initScrollbar();
        else customElements.whenDefined('custom-scrollbar').then(initScrollbar);
      }
    } catch (err) {
      console.error(err);
      this.unmountInfo(slide);
    }
  }

  unmountInfo(slide) {
    const container = slide.querySelector('[data-sfn-info]');
    if (!container || container.dataset.sfnLoaded !== 'true') return;
    container.innerHTML = '<div class="loading-spinner"></div>';
    delete container.dataset.sfnLoaded;
  }

  trimInfoContent(root) {
    root.querySelectorAll('.product__tax, .product__announcement').forEach((el) => el.remove());

    root
      .querySelectorAll(
        '.rating[data-value], .spr-badge, [class*="jdgm"], .stamped-badge, .stamped-product-reviews-badge, .yotpo, .loox-rating, .okeReviews, .oke-sr, .ryviu-widget, .shopify-app-block[data-block-handle*="badge"], .shopify-app-block[data-block-handle*="review"], .shopify-app-block[data-block-handle*="rating"], .shopify-app-block[id*="review"], .shopify-app-block[id*="badge"], .shopify-app-block[id*="judge"]'
      )
      .forEach((el) => el.remove());
    root
      .querySelectorAll('.product__description-container .product__block-title')
      .forEach((el) => el.remove());

    const descWords = parseInt(root.dataset.descWords || '0', 10);
    if (descWords > 0) {
      const desc = root.querySelector('.product__description-container .rte');
      const words = desc?.textContent.trim().split(/\s+/) || [];
      if (words.length > descWords) {
        desc.textContent = `${words.slice(0, descWords).join(' ')}…`;
      }
    }

    const attrs = root.querySelector('.product__attributes');
    if (!attrs) return;

    attrs.querySelectorAll('li').forEach((li) => {
      if (li.id && li.id.indexOf('Sku-') === 0) {
        li.remove();
        return;
      }
      const href = li.querySelector('a')?.getAttribute('href') || '';
      if (href.indexOf('vendors') !== -1) {
        Array.from(li.childNodes).forEach((node) => {
          if (node.nodeType === Node.TEXT_NODE) node.remove();
        });
      } else {
        li.remove();
      }
    });
  }

  simplifyActions(root) {
    root.querySelectorAll('.product__variant-picker').forEach((el) => el.remove());

    const container = root.closest('[data-sfn-info]') || root;
    if (container.dataset.hasVariants !== 'true') return;

    const chooseBlock = document.createElement('div');
    chooseBlock.className = 'product__info-block';
    const link = document.createElement('a');
    link.href = container.dataset.productUrl || '#';
    link.className = 'button w-100 shop-feed-new-popup__choose';
    link.textContent = container.dataset.chooseText || 'Choose options';
    chooseBlock.appendChild(link);

    const buyBlock =
      root.querySelector('product-form')?.closest('.product__info-block') ||
      root.querySelector('.product__actions-group');

    if (buyBlock) buyBlock.replaceWith(chooseBlock);
    else root.querySelector('product-info')?.appendChild(chooseBlock);

    this.revealChooseBlock(chooseBlock);
  }

  revealChooseBlock(block) {
    const show = () => {
      block.style.opacity = '1';
      block.style.transform = 'none';
    };

    const animate = window.motion?.animate;
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (this.isMobilePopup() || !animate || reduced) {
      show();
      return;
    }

    block.style.opacity = '0';
    requestAnimationFrame(() => {
      try {
        const anim = animate(
          block,
          { opacity: [0, 1], transform: ['translateY(40px)', 'translateY(0)'] },
          { duration: 0.5, easing: 'cubic-bezier(0, 0, 0.12, 0.88)' }
        );
        anim?.finished?.then(show).catch(show);
      } catch (err) {
        show();
      }
    });
  }

  pinFooter(root) {
    const scope = root.querySelector('product-info') || root;
    const viewDetails = scope.querySelector('.product__view-details');
    const wrap = root.parentNode; // .shop-feed-new-popup__info-wrap
    if (!viewDetails || !wrap) return;

    wrap.querySelector('.shop-feed-new-popup__footer')?.remove();

    const footer = document.createElement('div');
    footer.className = 'shop-feed-new-popup__footer';
    footer.appendChild(viewDetails);
    wrap.appendChild(footer);

    requestAnimationFrame(() => {
      const h = footer.offsetHeight;
      if (h) wrap.style.setProperty('--sfn-footer-h', `${h}px`);
    });
  }

  async loadExternalScripts(scope) {
    const scripts = Array.from(scope.querySelectorAll('script[src]'));
    if (!scripts.length) return;
    if (!ShopFeedNewPopup.loadedScripts) ShopFeedNewPopup.loadedScripts = new Map();
    await Promise.all(scripts.map((script) => this.loadScriptOnce(script.getAttribute('src'))));
  }

  loadScriptOnce(src) {
    if (!src) return Promise.resolve();
    const absoluteSrc = new URL(src, window.location.href).href;

    if (ShopFeedNewPopup.loadedScripts.has(absoluteSrc)) {
      return ShopFeedNewPopup.loadedScripts.get(absoluteSrc);
    }
    const existing = Array.from(document.head.querySelectorAll('script[src]')).find(
      (script) => script.src === absoluteSrc
    );
    if (existing) {
      const resolved = Promise.resolve();
      ShopFeedNewPopup.loadedScripts.set(absoluteSrc, resolved);
      return resolved;
    }
    const loadPromise = new Promise((resolve) => {
      const script = document.createElement('script');
      script.src = absoluteSrc;
      script.defer = true;
      script.onload = () => resolve();
      script.onerror = () => resolve();
      document.head.appendChild(script);
    });
    ShopFeedNewPopup.loadedScripts.set(absoluteSrc, loadPromise);
    return loadPromise;
  }

  close() {
    if (!this.isOpen) return;
    this.isOpen = false;

    this.classList.remove('is-open');
    document.body.classList.remove('overflow-hidden');
    document.removeEventListener('keydown', this._onKeydown);

    this.slides().forEach((slide) => {
      slide.querySelector('[data-sfn-media] deferred-media')?.pauseMedia?.();
      this.unmountInfo(slide);
    });

    window.setTimeout(() => {
      if (!this.isOpen) this.hidden = true;
    }, 300);
  }

  _onKeydown(e) {
    if (e.key === 'Escape') this.close();
    else if (e.key === 'ArrowLeft') this.slider?.navigateToPreviousSlide?.();
    else if (e.key === 'ArrowRight') this.slider?.navigateToNextSlide?.();
  }
}

if (!customElements.get('shop-feed-new-popup')) {
  customElements.define('shop-feed-new-popup', ShopFeedNewPopup);
}
